import { Avatar, Card, Col, Divider, Row, Space, Tag, Typography } from "antd";
import { format } from "date-fns";
import { VERIFICATION_STATUS_COLOR } from "commons/constants";

const { Title, Text, Paragraph } = Typography;

const FIELD_LABELS = {
  community_name: "Community Name",
  website: "Website",
  instagram_url: "Instagram URL",
  city: "City",
  city_code: "City Code",
  areas: "Areas",
  area_codes: "Area Codes",
  categories: "Categories",
  has_membership: "Has Membership",
  membership_name: "Membership Name",
  membership_type: "Membership Type",
  member_benefits: "Member Benefits",
  membership_required: "Membership Required",
  monthly_price: "Monthly Price (₹)",
};

const APP_SKIP_FIELDS = new Set([
  "name",
  "phone_number",
  "email_id",
  "account_types",
  "doc",
  "business_documents",
]);

function fmtDate(iso) {
  return iso && format(new Date(iso), "MMM d, yyyy HH:mm");
}

function renderAppValue(key, value) {
  if ((key === "website" && value) || (key === "instagram_url" && value))
    return (
      <a href={value} target="_blank" rel="noreferrer">
        {value}
      </a>
    );

  if (Array.isArray(value))
    return (
      <Space wrap size={4}>
        {value.map((v) => (
          <Tag key={v}>{v}</Tag>
        ))}
      </Space>
    );
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (key === "monthly_price") return `₹${value}`;
  return String(value);
}

/** Reusable labeled field block */
function Field({ label, children, span = 1 }) {
  return (
    <Col xs={24} sm={24 / span > 12 ? 24 : 12} md={span === 1 ? 8 : 24}>
      <div style={{ marginBottom: 16 }}>
        <Text
          type="secondary"
          style={{ fontSize: 12, display: "block", marginBottom: 2 }}
        >
          {label}
        </Text>
        <div style={{ fontWeight: 500 }}>{children}</div>
      </div>
    </Col>
  );
}

function SectionTitle({ children }) {
  return (
    <Col xs={24}>
      <Text
        strong
        style={{
          fontSize: 13,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--color-text-secondary, #888)",
        }}
      >
        {children}
      </Text>
      <Divider style={{ margin: "8px 0 16px" }} />
    </Col>
  );
}

function AccountDetailView({ account }) {
  const app = account.initial_application;
  const vendor = account.vendor;

  const appFields = app
    ? Object.entries(app).filter(
        ([k, v]) => !APP_SKIP_FIELDS.has(k) && v !== null && v !== undefined
      )
    : [];

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      {/* ── Account Card ──────────────────────────────────────────── */}
      <Card>
        {account.cover_url && (
          <div
            style={{
              margin: "-24px -24px 20px",
              borderRadius: "8px 8px 0 0",
              overflow: "hidden",
              height: 130,
            }}
          >
            <img
              src={account.cover_url}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
        )}

        {/* Avatar + name */}
        <Row gutter={[16, 0]} align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Avatar size={72} src={account.logo_url}>
              {account.name?.[0]?.toUpperCase()}
            </Avatar>
          </Col>
          <Col flex={1}>
            <Title level={4} style={{ margin: 0 }}>
              {account.name}
            </Title>
            {account.display_name !== account.name && (
              <Text type="secondary">{account.display_name}</Text>
            )}
            {account.description && (
              <Text
                type="secondary"
                style={{ display: "block", marginTop: 4, fontSize: 13 }}
              >
                {account.description}
              </Text>
            )}
          </Col>
        </Row>

        {/* Account fields */}
        <Row gutter={[16, 0]}>
          <Col xs={24}>
            <div style={{ marginBottom: 16 }}>
              <Text
                type="secondary"
                style={{ fontSize: 12, display: "block", marginBottom: 2 }}
              >
                Account ID
              </Text>
              <Paragraph
                copyable={{ text: account.account_id }}
                style={{
                  margin: 0,
                  fontFamily: "monospace",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {account.account_id}
              </Paragraph>
            </div>
          </Col>

          <Field label="Email">{account.email}</Field>
          <Field label="Phone">{account.phone_number}</Field>
          <Field label="Account Types">
            <Space wrap size={4}>
              {account.account_types?.map((t) => (
                <Tag key={t}>{t}</Tag>
              ))}
            </Space>
          </Field>
          <Field label="Verification Status">
            <Tag color={VERIFICATION_STATUS_COLOR[account.verification_status]}>
              {account.verification_status}
            </Tag>
          </Field>
          <Field label="Active">
            <Tag color={account.is_active ? "success" : "red"}>
              {account.is_active ? "Active" : "Inactive"}
            </Tag>
          </Field>
          <Field label="Submitted On">
            {fmtDate(account.audit?.created_at)}
          </Field>
          <Field label="Last Updated">
            {fmtDate(account.audit?.updated_at)}
          </Field>

          {account.reason_for_rejection && (
            <Col xs={24}>
              <div style={{ marginBottom: 16 }}>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginBottom: 2 }}
                >
                  Reason for Rejection
                </Text>
                <Text
                  style={{
                    fontWeight: 500,
                    color: "var(--ant-color-error, #ff4d4f)",
                  }}
                >
                  {account.reason_for_rejection}
                </Text>
              </div>
            </Col>
          )}
        </Row>
      </Card>

      {/* ── Initial Application Card ───────────────────────────────── */}
      {(appFields.length > 0 || app?.business_documents?.length > 0) && (
        <Card title="Initial Application">
          <Row gutter={[16, 0]}>
            {appFields.map(([key, value]) => {
              return (
                <Col key={key} xs={24} sm={12}>
                  <div style={{ marginBottom: 16 }}>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 12,
                        display: "block",
                        marginBottom: 2,
                      }}
                    >
                      {FIELD_LABELS[key] ?? key.replace(/_/g, " ")}
                    </Text>
                    <div style={{ fontWeight: 500 }}>
                      {renderAppValue(key, value)}
                    </div>
                  </div>
                </Col>
              );
            })}
            {app?.business_documents?.length > 0 && (
              <>
                <SectionTitle>Business Documents</SectionTitle>
                {app.business_documents.map((doc, i) => (
                  <Field key={i} label={doc.type ?? `Document ${i + 1}`}>
                    {doc.value}
                  </Field>
                ))}
              </>
            )}
          </Row>
        </Card>
      )}

      {/* ── Vendor Card ───────────────────────────────────────────── */}
      {vendor && (
        <Card title="Business / Vendor Profile">
          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <div style={{ marginBottom: 16 }}>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginBottom: 2 }}
                >
                  Vendor ID
                </Text>
                <Paragraph
                  copyable={{ text: vendor.vendor_id }}
                  style={{
                    margin: 0,
                    fontFamily: "monospace",
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  {vendor.vendor_id}
                </Paragraph>
              </div>
            </Col>

            <Field label="Business Name">{vendor.business_name}</Field>
            <Field label="Display Name">{vendor.display_name}</Field>
            <Field label="Email">{vendor.email}</Field>
            <Field label="Phone">{vendor.phone_number}</Field>
            {vendor.alternate_phone_number && (
              <Field label="Alt. Phone">{vendor.alternate_phone_number}</Field>
            )}
            {vendor.website && (
              <Field label="Website">
                <a href={vendor.website} target="_blank" rel="noreferrer">
                  {vendor.website}
                </a>
              </Field>
            )}
            <Field label="Business Type">{vendor.business_type}</Field>
            <Field label="Provider Type">{vendor.service_provider_type}</Field>
            <Field label="Type">{vendor.type}</Field>
            <Field label="Onboarding Status">
              <Tag
                color={
                  vendor.onboarding_status === "COMPLETED"
                    ? "success"
                    : "warning"
                }
              >
                {vendor.onboarding_status}
              </Tag>
            </Field>
            <Field label="Verification">
              <Tag
                color={VERIFICATION_STATUS_COLOR[vendor.verification_status]}
              >
                {vendor.verification_status}
              </Tag>
            </Field>
            <Field label="Active">
              <Tag color={vendor.is_active ? "success" : "default"}>
                {vendor.is_active ? "Active" : "Inactive"}
              </Tag>
            </Field>
            <Field label="Commission Rate">{vendor.commission_rate}%</Field>
            <Field label="Payment Terms">{vendor.payment_terms}</Field>
            <Field label="Marketplace Bookings">
              <Tag
                color={
                  vendor.accepts_marketplace_bookings ? "success" : "default"
                }
              >
                {vendor.accepts_marketplace_bookings
                  ? "Accepted"
                  : "Not Accepted"}
              </Tag>
            </Field>
            <Field label="Auto Accept Bookings">
              <Tag color={vendor.auto_accept_bookings ? "success" : "default"}>
                {vendor.auto_accept_bookings ? "Yes" : "No"}
              </Tag>
            </Field>
            {vendor.slug && <Field label="Slug">{vendor.slug}</Field>}
            {vendor.description && (
              <Col xs={24}>
                <div style={{ marginBottom: 16 }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginBottom: 2 }}
                  >
                    Description
                  </Text>
                  <Text style={{ fontWeight: 500 }}>{vendor.description}</Text>
                </div>
              </Col>
            )}
          </Row>

          {vendor.address && (
            <>
              <Divider style={{ margin: "8px 0 16px" }} />
              <Row gutter={[16, 0]}>
                <SectionTitle>Address</SectionTitle>
                <Field label="Street">{vendor.address.address_line_1}</Field>
                <Field label="City">{vendor.address.city}</Field>
                <Field label="State">{vendor.address.state}</Field>
                <Field label="ZIP">{vendor.address.zip_code}</Field>
                <Field label="Country">{vendor.address.country_code}</Field>
              </Row>
            </>
          )}

          {vendor.business_documents?.length > 0 && (
            <>
              <Divider style={{ margin: "8px 0 16px" }} />
              <Row gutter={[16, 0]}>
                <SectionTitle>Business Documents</SectionTitle>
                {vendor.business_documents.map((doc, i) => (
                  <Field
                    key={i}
                    label={doc.document_type ?? `Document ${i + 1}`}
                  >
                    {doc.document_value}
                  </Field>
                ))}
              </Row>
            </>
          )}

          <Divider style={{ margin: "8px 0 16px" }} />
          <Row gutter={[16, 0]}>
            <SectionTitle>Audit</SectionTitle>
            <Field label="Created By">{vendor.audit?.created_by}</Field>
            <Field label="Created On">
              {fmtDate(vendor.audit?.created_at)}
            </Field>
            <Field label="Updated By">{vendor.audit?.updated_by}</Field>
            <Field label="Updated On">
              {fmtDate(vendor.audit?.updated_at)}
            </Field>
          </Row>
        </Card>
      )}
    </Space>
  );
}

export default AccountDetailView;
